// MediaSilo public review links ("quicklinks"). Keep in sync with the backend
// copy (../autoclipper-backend/src/lib/mediasilo/url.js) — both run
// mediasiloLinkTestVectors.json.
//
// https://app.mediasilo.com/review/<24-hex id>[/f/<folder uuid>] lists a review
// or one of its folders; ?asset=<uuid> is the backend's canonical single-video
// form (MediaSilo itself has no per-asset page URL).
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const QUICKLINK = '[0-9a-fA-F]{24}';
const REVIEW_PATH = new RegExp(`^/review/(${QUICKLINK})(?:/f/(${UUID}))?/?$`);
const ASSET_ID = new RegExp(`^${UUID}$`);

export type MediasiloLink =
  | { kind: 'not_mediasilo' }
  | { kind: 'unsupported' }
  | { kind: 'review'; quicklinkId: string; folderId: string | null; assetId: string | null };

function parseUrl(value: string): URL | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
}

function isMediasiloHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'mediasilo.com' || host.endsWith('.mediasilo.com');
}

export function isMediasiloUrl(value: string): boolean {
  const url = parseUrl(value);
  return Boolean(url && isMediasiloHost(url.hostname));
}

export function parseMediasiloUrl(value: string): MediasiloLink {
  const url = parseUrl(value);
  if (!url || !isMediasiloHost(url.hostname)) return { kind: 'not_mediasilo' };
  if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== 'app.mediasilo.com'
    || url.username || url.password || url.port) {
    return { kind: 'unsupported' };
  }
  const match = url.pathname.match(REVIEW_PATH);
  if (!match) return { kind: 'unsupported' };
  const asset = url.searchParams.get('asset');
  if (asset !== null && !ASSET_ID.test(asset)) return { kind: 'unsupported' };
  return {
    kind: 'review',
    quicklinkId: match[1].toLowerCase(),
    folderId: match[2] ? match[2].toLowerCase() : null,
    assetId: asset ? asset.toLowerCase() : null,
  };
}
